<script>
  import { Bread, store, imgSrc, phonePrettify, phonePurify, Link, Button, getCity, debounce } from '$client';
  import { onNavigate } from '$app/navigation';
  import { page } from '$app/stores';
  import { onMount } from 'svelte';
  export let bread;
  export let data;

  const { city, search, cities, burger: isOpen } = store;

  const removeCity = url => url.replace(/^\/gorod-[a-z0-9-_]+\/?/, '/');
  $: pathName = removeCity($page.url.pathname);
  $: defaultCity = getCity($page.url.origin);

  $: transform = city => ({
    header: city.header,
    href: defaultCity === city.url ? pathName : '/gorod-' + city.url + pathName,
    isCity: true,
  });

  $: cityBlock = {
    ...transform($city),
    block: { data: { attributes: { links: $cities.map(city => transform(city.attributes)) } } },
  };

  $: links = [cityBlock, ...data.links];

  let mounted = false;
  isOpen.subscribe(v => {
    if (!mounted) return v;
    try {
      if (v) document.body.style.overflowY = 'hidden';
      else document.body.style.overflowY = 'auto';
    } catch {}
    return v;
  });

  const reopen = debounce(() => (navigation = false), 250);
  let navigation = false;
  onNavigate(() => {
    navigation = true;
    try {
      document.body.style.overflowY = 'auto';
    } catch {}
    reopen();
    isOpen.set(false);
  });

  onMount(() => {
    if ($isOpen) document.body.style.overflowY = 'hidden';
    else document.body.style.overflowY = 'auto';

    const stickyElm = document.querySelector('.header-menu');
    const observer = new IntersectionObserver(
      ([e]) => e.target.classList.toggle('sticky-position', e.intersectionRatio < 1),
      {
        threshold: [1],
      },
    );

    mounted = true;
    observer.observe(stickyElm);
    return () => {
      mounted = false;
      observer.unobserve(stickyElm);
    };
  });
</script>

<header>
  <div class="header-menu">
    <address class="container header-contacts">
      <a rel="nofollow" class="color-button" href="tel:{phonePurify($city.phone)}">{phonePrettify($city.phone)}</a>
      <time datetime="Mo-Su 9:00-21:00">{$city.workTime}</time>
      <div>
        {#each $city.socials as { href, img }}
          <a
            class="push-button"
            rel="nofollow"
            target="_blank"
            {href}
            style="background-image: url({imgSrc(img.data.attributes.url)})"
            aria-label={img.data.attributes.alternativeText}>
          </a>
        {/each}
      </div>
    </address>

    <div class="container">
      <Link href="/" attributes={{ class: 'push-button' }}>
        <img
          src={imgSrc(data.img.data.attributes.url)}
          alt={data.img.data.attributes.alternativeText}
          width="112"
          height="37" />
      </Link>

      <div class="header-burger-menu container" data-open={$isOpen}>
        <ul>
          {#each links as link}
            <li class="header-hover-open" data-nav={navigation} data-open={!!link.activeSection}>
              {#if !(link.block || link.blocks)}
                <Link href={link.href}>{link.header}</Link>
              {:else}
                <button on:click={() => ([link.activeSection, links] = [!link.activeSection, links])} type="button">
                  {link.header}
                </button>
                <nav class={link.blocks ? 'deep-header-nav' : 'header-nav'}>
                  <div>
                    <ul>
                      {#each link.blocks ?? link.block.data.attributes.links as item, i}
                        <li data-open={link.activeElement === i}>
                          {#if item.block}
                            <div>
                              <img
                                src={imgSrc(item.img.data.attributes.url)}
                                alt={item.img.data.attributes.alternativeText}
                                width="30"
                                height="30" />
                              <button
                                type="button"
                                on:click={() =>
                                  ([link.activeElement, links] = [link.activeElement === i ? -1 : i, links])}>
                                {item.header}
                              </button>
                            </div>
                            <ul>
                              {#each item.block.data.attributes.links as el}
                                <li>
                                  <Link href={el.href}>{el.header}</Link>
                                </li>
                              {/each}
                              {#if item.block.data.attributes.button}
                                <li class="header-more-button-deep">
                                  <Link href={item.block.data.attributes.button.href}
                                    >{item.block.data.attributes.button.header}</Link>
                                </li>
                              {/if}
                            </ul>
                          {:else if item.isCity}
                            <a href={item.href}>{item.header} </a>
                          {:else}
                            <img
                              src={imgSrc(item.img.data.attributes.url)}
                              alt={item.img.data.attributes.alternativeText}
                              width="30"
                              height="30" />
                            <Link href={item.href}>{item.header}</Link>
                          {/if}
                        </li>
                      {/each}
                      {#if link.block?.data.attributes.button}
                        <li class="header-more-button">
                          <Link href={link.block.data.attributes.button.href}
                            >{link.block.data.attributes.button.header}</Link>
                        </li>
                      {/if}
                    </ul>
                  </div>
                </nav>
              {/if}
            </li>
          {/each}

          <li data-mobile-only class="mobile-phone">
            <a rel="nofollow" class="color-button" href="tel:{phonePurify($city.phone)}">
              {phonePrettify($city.phone)}
            </a>
            <p>{$city.workTime}</p>
          </li>

          <li data-mobile-only>
            {#each $city.socials as elem}
              <a class="push-button" target="_blank" rel="nofollow" href={elem.href}>
                <img
                  src={imgSrc(elem.img.data.attributes.url)}
                  alt={elem.img.data.attributes.alternativeText}
                  width="24"
                  height="24" />
              </a>
            {/each}
          </li>
        </ul>
      </div>

      <div class="header-buttons">
        <button
          type="button"
          class="header-search-button"
          aria-label="Управление поисковым меню"
          on:click={() => search.set(true)} />
        <Button type="transparent-white" action={data.button[0].modal} title={data.button[0].header} />
        <button
          aria-label="Управление мобильным меню"
          type="button"
          class="header-burger-button"
          data-open={$isOpen === true}
          on:click={() => isOpen.update(v => !v)}>
          <span />
          <span />
          <span />
        </button>
      </div>
    </div>
  </div>

  {#if !$page.url.pathname.includes('errors') && !$page.error}
    <Bread data={Object.entries(bread)} />
  {/if}
</header>

<style lang="scss">
  .header-more-button-deep {
    grid-column: 1 / 3;

    :global(div) {
      display: none;
    }

    :global(a) {
      display: flex;
      gap: 20px;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      max-width: 250px;
      margin-top: 20px;
      padding: 10px 20px;
      color: #2c333c !important;
      font-family: Roboto, sans-serif;
      white-space: wrap;
      background: #22eaac;
      border-radius: 5px;

      @media only screen and (max-width: 575.98px) {
        width: 100%;
        max-width: 100%;
        margin-top: 0;
      }

      &:hover {
        transform: scale(0.95);
      }

      &::after {
        min-width: 16px;
        min-height: 16px;
        background-image: url('https://admin.uberi.pro/uploads/Open_Achieve_5a4f57ec76.svg');
        background-repeat: no-repeat;
        background-position: center center;
        background-size: contain;
        filter: invert(100%);
        content: '';
      }
    }
  }

  .header-more-button {
    display: flex;
    gap: 10px;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    max-width: 250px;
    margin-top: 15px;
    padding: 10px 20px;
    color: #2c333c;
    white-space: wrap;
    text-align: center;
    background: #22eaac;
    border-radius: 5px;

    @media only screen and (max-width: 575.98px) {
      width: 100%;
      max-width: 100%;
      margin-top: 0;
    }

    :global(div) {
      display: none;
    }

    :global(a) {
      color: #2c333c !important;
      font-size: 16px;
      font-family: Roboto, sans-serif;
    }

    &::after {
      min-width: 16px;
      min-height: 16px;
      background-image: url('https://admin.uberi.pro/uploads/Open_Achieve_5a4f57ec76.svg');
      background-repeat: no-repeat;
      background-position: center center;
      background-size: contain;
      filter: invert(100%);
      content: '';
    }

    &:hover {
      transform: scale(0.95);
    }
  }

  header {
    display: contents;
  }

  .header-menu {
    position: sticky;
    top: -2px;
    z-index: 100;
    display: flex;
    flex-direction: column;
    width: 100%;
    padding-bottom: 24px;
    background-color: rgb(44 51 60 / 100%);

    > div {
      display: inline-flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      padding-top: 24px;
    }
  }

  address {
    display: inline-flex;
    gap: 20px;
    align-items: center;
    padding-block: 16px;
    border-bottom: 2px solid rgb(83 91 101 / 100%);

    @media only screen and (max-width: 575.98px) {
      display: none;
    }

    time {
      color: #fff;
      font-size: 14px;
      font-family: Roboto, sans-serif;
    }

    :global(> a),
    :global(> div) {
      color: #fff;
      font-size: 14px;
      font-family: Roboto, sans-serif;
    }

    div {
      display: inline-flex;
      gap: 20px;
      align-items: center;

      > a {
        position: relative;
        width: 24px;
        height: 24px;
        background-position: center center;
        background-size: cover;
      }
    }
  }

  .header-buttons {
    position: relative;
    display: flex;
    gap: 25px;
    align-items: center;

    :global(button[data-type='transparent-white']) {
      padding: 12px;

      @media only screen and (max-width: 1199.98px) {
        width: 26px;
        height: 26px;
        padding: 0;
        color: #fff;
        font-size: 0;
        background-image: url('https://admin.uberi.pro/uploads/phone_ba385911ea.svg');
        background-repeat: no-repeat;
        background-position: center;
        background-size: 26px;
        border: none;
      }
    }

    .header-search-button {
      position: relative;
      width: 30px;
      height: 30px;
      background-image: url('https://admin.uberi.pro/uploads/search_f121ebae2e.svg');
      background-repeat: no-repeat;
      background-position: center;
      background-size: 30px;
    }

    .header-burger-button {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: space-between;
      width: 30px;
      height: 20px;

      @media only screen and (min-width: 991.98px) {
        display: none;
      }

      span {
        width: 100%;
        height: 4px;
        background-color: #fff;
      }

      &[data-open='true'] {
        position: relative;
        align-items: center;
        justify-content: center;

        span {
          position: absolute;
          display: none;
        }

        span:first-child {
          display: block;
          transform: rotate(45deg);
        }

        span:last-child {
          display: block;
          transform: rotate(-45deg);
        }
      }
    }
  }

  .header-burger-menu {
    @media only screen and (max-width: 991.98px) {
      &[data-open='false'] {
        display: none;
      }
    }

    > ul {
      position: relative;
      display: inline-flex;
      gap: 30px;
      align-items: center;
      background-color: transparent;
      border: none;

      li[data-mobile-only] {
        @media only screen and (min-width: 991.98px) {
          display: none;
        }
      }

      > li {
        position: relative;
      }

      > li > button::after {
        background-image: url('https://admin.uberi.pro/uploads/arrow_f2d0451a1e.svg');
      }

      > li[data-open='true'] {
        @media only screen and (max-width: 991.98px) {
          nav {
            display: flex;
          }

          > button {
            color: rgb(34 234 172 / 100%) !important;
          }

          > button::after {
            background-image: url('https://admin.uberi.pro/uploads/arrow_f2d0451a1e.svg');
            transform: rotate(180deg);
          }
        }
      }

      .header-hover-open[data-nav='false']:hover {
        @media only screen and (min-width: 991.98px) {
          nav {
            display: flex;
          }

          > button {
            color: rgb(34 234 172 / 100%) !important;
          }

          > button::after {
            background-image: url('https://admin.uberi.pro/uploads/arrow_f2d0451a1e.svg');
            transform: rotate(180deg);
          }
        }
      }

      :global(a),
      :global(div) {
        color: #fff;
        font-size: 16px;
        font-family: Roboto, sans-serif;

        &:is(a):hover {
          color: rgb(34 234 172 / 100%);
        }

        @media only screen and (max-width: 991.98px) {
          font-size: 22px;
        }

        @media only screen and (max-width: 380.98px) {
          font-size: 18px;
        }
      }

      button {
        display: inline-flex;
        align-items: center;
        color: #fff;
        font-size: 16px;
        font-family: Roboto, sans-serif;

        @media only screen and (max-width: 991.98px) {
          justify-content: space-between;
          width: 100%;
          margin-left: auto;
          font-size: 22px;
        }

        @media only screen and (max-width: 380.98px) {
          font-size: 18px;
        }

        &::after {
          display: block;
          width: 15px;
          height: 15px;
          margin-left: 5px;
          background-repeat: no-repeat;
          background-position: center;
          content: '';
        }
      }

      > li:first-child > button {
        display: inline-flex;
        gap: 5px;

        @media only screen and (max-width: 991.98px) {
          width: auto;
        }

        &::after {
          content: none;
        }

        &::before {
          display: block;
          width: 16px;
          height: 16px;
          background-image: url('https://admin.uberi.pro/uploads/header_mark_de25e20fcd.svg');
          background-position: center;
          background-size: 16px;
          content: '';

          @media only screen and (max-width: 991.98px) {
            width: 25px;
            height: 25px;
            background-size: 25px;
          }
        }
      }

      nav {
        display: none;
      }
    }
  }

  @media only screen and (max-width: 991.98px) {
    .mobile-phone {
      color: #fff;
      font-family: Roboto, sans-serif;

      p {
        margin-top: 5px;
      }
    }

    .header-burger-menu {
      position: absolute;
      top: 100%;
      left: 0;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: flex-start;
      width: 100%;
      height: calc(100vh - 100% + 2px - 68px);
      padding-top: 15px;
      overflow-y: auto;
      background: rgb(44 51 60 / 100%);

      > ul {
        position: relative;
        display: flex;
        flex-direction: column;
        gap: 15px;
        align-items: flex-start;
        justify-content: flex-start;
        width: 100%;
      }

      > ul > li {
        width: 100%;
        padding-bottom: 16px;

        &:last-child {
          display: inline-flex;
          gap: 15px;
          align-items: center;
        }

        &:not(:last-child) {
          border-bottom: 2px solid #fff;
        }
      }

      > ul > li > nav > div {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        width: 100%;
        margin-top: 15px;

        > ul > li,
        > ul > li > div {
          position: relative;
          display: inline-flex;
          gap: 10px;
          align-items: center;
          width: 100%;
          font-family: Roboto, sans-serif;
          padding-block: 5px;
        }

        img {
          width: 22px;
          object-fit: scale-down;
        }
      }

      .deep-header-nav > div > ul > li {
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-start;

        &[data-open='false'] > ul {
          display: none;
        }

        &[data-open='false'] button::after {
          display: block;
          width: 14px;
          height: 14px;
          margin-left: 5px;
          background-image: url('https://admin.uberi.pro/uploads/plus_63406f6686.svg');
          background-repeat: no-repeat;
          background-position: center;
          background-size: 14px;
          content: '';
        }

        &[data-open='true'] button::after {
          display: block;
          width: 14px;
          height: 14px;
          margin-left: 5px;
          background-image: url('https://admin.uberi.pro/uploads/minus_65877b6b22_5eb5c760b8.svg');
          background-repeat: no-repeat;
          background-position: center;
          background-size: 14px;
          content: '';
        }

        &[data-open='true'] button {
          color: rgb(34 234 172 / 100%);
        }

        > ul {
          margin-block: 10px;
          display: flex;
          flex-direction: column;
          gap: 20px;

          > li > :global(a),
          > li > :global(div) {
            width: 100%;
            color: #fff;
            font-size: 18px;
            font-family: Roboto, sans-serif;

            &:hover {
              color: rgb(34 234 172 / 100%);
            }
          }
        }
      }
    }

    :global(.sticky-position .header-burger-menu) {
      height: calc(100vh - 100% + 2px) !important;
    }
  }

  @media only screen and (min-width: 991.98px) {
    nav {
      position: absolute;
      left: 50%;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      padding: 40px;
      padding-top: 25px;
      transform: translateX(-50%);

      @media only screen and (min-width: 1199.98px) {
        padding: 100px;
        padding-top: 25px;
      }

      > div {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        padding: 25px;
        background-color: rgb(44 51 60 / 100%);
        border-radius: 10px;
        box-shadow: 0 -4px 8px 0 rgb(0 0 0 / 25%);

        > ul {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }

        > ul > li {
          display: inline-flex;
          gap: 10px;
          align-items: center;
          width: 100%;
          font-family: Roboto, sans-serif;
          white-space: nowrap;
          padding-block: 10px;
        }
      }

      img {
        object-fit: scale-down;
      }
    }

    .deep-header-nav {
      > div {
        min-width: 920px;
        padding-top: 10px;

        @media only screen and (max-width: 1199.98px) {
          min-width: 620px;
        }
      }

      > div > ul {
        li[data-open='false'] > ul {
          display: none;
        }

        li[data-open='true'] button {
          color: rgb(34 234 172 / 100%);
        }

        &:not(:has(> li[data-open='true'])) > li:first-child {
          button {
            color: rgb(34 234 172 / 100%);
          }

          > ul {
            display: grid;
          }
        }
      }

      > div > ul > li > ul {
        position: absolute;
        top: 10px;
        left: 0;
        z-index: 2;
        display: grid;
        grid-auto-rows: min-content;
        grid-template-columns: repeat(2, 1fr);
        gap: 16px;
        width: calc(920px - 100% - 40px);
        height: 100%;
        max-height: 100%;
        margin-left: calc(100% + 10px);
        padding-left: 25px;
        overflow-y: auto;
        border-left: 2px solid rgb(83 91 101 / 100%);

        @media only screen and (max-width: 1199.98px) {
          grid-template-columns: repeat(1, 1fr);
          width: calc(620px - 100% - 40px);
        }

        :global(a),
        :global(div) {
          width: auto;
          width: 100%;
          color: #fff;
          font-size: 16px;
          font-family: Roboto, sans-serif;
          white-space: normal;

          &:is(a):hover {
            color: rgb(34 234 172 / 100%);
          }

          @media only screen and (max-width: 767.98px) {
            white-space: break-spaces;
          }
        }
      }
    }
  }
</style>
